import React, { useCallback, useState } from 'react';

import { IShowComment } from '../../dtos/IPostProjectProps';
import api from '../../services/api';
import {
  ActionButton,
  Actions,
  Avatar,
  Body,
  ConfirmRow,
  Container,
  DangerButton,
  EditForm,
  EditedMark,
  ErrorText,
} from './styles';

const DEFAULT_AVATAR =
  'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png';

interface IPostCommentProps {
  comment: IShowComment;
  /** Só o autor vê editar e excluir. */
  isOwn: boolean;
  onUpdated: (comment: IShowComment) => void;
  onDeleted: (comment_id: string) => void;
}

/**
 * Um comentário do feed, com edição e exclusão para quem escreveu.
 *
 * Vale lembrar o que cada ação faz com os pontos, porque não é simétrico:
 * editar não mexe em nada, excluir devolve os 100 pontos do comentário. Quem
 * apaga precisa saber disso antes de confirmar — daí o aviso na confirmação,
 * e não só um "tem certeza?".
 */
const PostComment: React.FC<IPostCommentProps> = ({
  comment,
  isOwn,
  onUpdated,
  onDeleted,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(comment.comment);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const startEditing = useCallback(() => {
    setDraft(comment.comment);
    setError('');
    setIsConfirmingDelete(false);
    setIsEditing(true);
  }, [comment.comment]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setError('');
  }, []);

  const saveEdit = useCallback(async () => {
    const trimmed = draft.trim();

    if (!trimmed) {
      setError('O comentário não pode ficar vazio.');
      return;
    }

    // Salvar sem ter mudado nada só gastaria uma requisição e marcaria o
    // comentário como editado à toa.
    if (trimmed === comment.comment) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const { data } = await api.patch(`/posts/comments/${comment.id}`, {
        comment: trimmed,
      });

      onUpdated(data);
      setIsEditing(false);
    } catch {
      setError('Não foi possível salvar. Tente de novo.');
    } finally {
      setIsSaving(false);
    }
  }, [draft, comment.comment, comment.id, onUpdated]);

  const confirmDelete = useCallback(async () => {
    setIsSaving(true);
    setError('');

    try {
      await api.delete(`/posts/comments/${comment.id}`);
      onDeleted(comment.id);
    } catch {
      setError('Não foi possível excluir. Tente de novo.');
      setIsSaving(false);
      setIsConfirmingDelete(false);
    }
  }, [comment.id, onDeleted]);

  return (
    <Container>
      <Avatar $avatarSrc={comment.user_avatar || DEFAULT_AVATAR} />

      <Body>
        <h1>{comment.user_name}</h1>
        <span>{comment.user_school}</span>

        {isEditing ? (
          <EditForm>
            <textarea
              value={draft}
              onChange={event => setDraft(event.target.value)}
              aria-label="Editar comentário"
              disabled={isSaving}
            />
            <Actions>
              <ActionButton type="button" onClick={saveEdit} disabled={isSaving}>
                {isSaving ? 'Salvando…' : 'Salvar'}
              </ActionButton>
              <ActionButton
                type="button"
                onClick={cancelEditing}
                disabled={isSaving}
              >
                Cancelar
              </ActionButton>
            </Actions>
          </EditForm>
        ) : (
          <p>
            {comment.comment}
            {comment.edited_at && <EditedMark>(editado)</EditedMark>}
          </p>
        )}

        {isOwn && !isEditing && !isConfirmingDelete && (
          <Actions>
            <ActionButton type="button" onClick={startEditing}>
              Editar
            </ActionButton>
            <DangerButton
              type="button"
              onClick={() => setIsConfirmingDelete(true)}
            >
              Excluir
            </DangerButton>
          </Actions>
        )}

        {isConfirmingDelete && (
          <ConfirmRow>
            <span>Excluir e devolver os 100 pontos?</span>
            <DangerButton
              type="button"
              onClick={confirmDelete}
              disabled={isSaving}
            >
              {isSaving ? 'Excluindo…' : 'Excluir'}
            </DangerButton>
            <ActionButton
              type="button"
              onClick={() => setIsConfirmingDelete(false)}
              disabled={isSaving}
            >
              Cancelar
            </ActionButton>
          </ConfirmRow>
        )}

        {error && <ErrorText>{error}</ErrorText>}
      </Body>
    </Container>
  );
};

export default PostComment;
